import React, { useState, ChangeEvent, useEffect } from 'react';
import {
	TextField,
	Button,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	SelectChangeEvent,
	DialogActions,
	Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import Grid from '@mui/material/Grid2';
import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
import { deleteWholesaleImage, updateWholesale } from '../../../services/api/Wholesale';

interface CustomField {
	fieldType: 'text' | 'file';
	fieldName: string;
	filePath?: string | File;
	fieldValue?: string;
	_id?: string;
	isDeleted?: boolean; // Add this field to mark for deletion
}

interface FormData {
	_id: string;
	productId: string;
	quantity: number;
	fullName: string;
	email: string;
	mobileNo: string;
	message: string;
	status: number;
	followupStatus: string;
	Notification: boolean;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
	customFields: CustomField[];
}

interface CreateProductsProps {
	product: FormData | null;
	isDisabled?: boolean;
	handleCloseDialog: () => void;
	fetchData: () => void;
}

const CreateProducts: React.FC<CreateProductsProps> = ({
	product,
	isDisabled,
	fetchData,
	handleCloseDialog,
}) => {
	const [formData, setFormData] = useState<FormData>({
		_id: '',
		productId: '',
		quantity: 0,
		fullName: '',
		email: '',
		mobileNo: '',
		message: '',
		status: 1,
		followupStatus: 'New',
		Notification: false,
		createdBy: '',
		createdAt: '',
		updatedAt: '',
		customFields: [],
	});

	const [upcomeinDeleteImage, setUpcomeinDeleteImage] = useState<string[]>([]);
	const [isOtherSelected, setIsOtherSelected] = useState(false);
	const [otherFollowupStatus, setOtherFollowupStatus] = useState('');
	const [loading, setLoading] = useState(false);

	const predefinedOptions = [
		'New',
		'Under Review',
		'Awaiting Supplier',
		'Quotation Sent',
		'Follow-up Pending',
		'Confirmed',
		'Closed',
		'Cancelled',
		'Other',
	];

	useEffect(() => {
		if (product) {
			setFormData(product);
			setIsOtherSelected(!predefinedOptions.includes(product.followupStatus));
			setOtherFollowupStatus(
				!predefinedOptions.includes(product.followupStatus) ? product.followupStatus : ''
			);
		} else {
			// Reset fields when product is null
			setFormData({
				_id: '',
				productId: '',
				quantity: 0,
				fullName: '',
				email: '',
				mobileNo: '',
				message: '',
				status: 1,
				followupStatus: 'New',
				Notification: false,
				createdBy: '',
				createdAt: '',
				updatedAt: '',
				customFields: [],
			});
			setIsOtherSelected(false);
			setOtherFollowupStatus('');
			setUpcomeinDeleteImage([]);
		}
	}, [product]);

	const handleChange = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
	) => {
		const { name, value } = e.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleFollowupStatusChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value as string;
		setFormData((prevData) => ({ ...prevData, followupStatus: value }));
		setIsOtherSelected(value === 'Other');
	};

	const handleOtherFollowupStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setOtherFollowupStatus(value);
	};

	const deleteMarkedImages = async () => {
		const promises = upcomeinDeleteImage.map(async (field) => {
			const data = { imageUrl: field };
			try {
				const response = await deleteWholesaleImage(formData._id, data);
				if (!response.success) {
					toast.error(`Failed to remove image`);
				}
			} catch (error) {
				console.error(`Error deleting image`, error);
				toast.error('Error occurred while deleting an image.');
			}
		});

		await Promise.all(promises); // Wait for all deletions to complete
	};

	const handleCustomFieldChange = async (index: number, value: string | File) => {
		const newCustomFields = [...formData.customFields];

		if (typeof value === 'string') {
			// Add the existing file path to upcomeinDeleteImage before updating
			if (newCustomFields[index].filePath) {
				setUpcomeinDeleteImage((prev) => [
					...prev,
					newCustomFields[index].filePath as string,
				]);
			}
			newCustomFields[index].fieldValue = value;
			setFormData((prevData) => ({
				...prevData,
				customFields: newCustomFields,
			}));
		} else {
			// Add the existing file path to upcomeinDeleteImage before uploading a new one
			if (newCustomFields[index].filePath && formData._id) {
				setUpcomeinDeleteImage((prev) => [
					...prev,
					newCustomFields[index].filePath as string,
				]);
				newCustomFields[index].isDeleted = true;
			}

			// Upload new image
			newCustomFields[index].filePath = value;
			newCustomFields[index].fieldValue = value as unknown as string;
			setFormData((prevData) => ({
				...prevData,
				customFields: newCustomFields,
			}));
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const formDataToSend = new FormData();

		formDataToSend.append('quantity', formData.quantity.toString());
		formDataToSend.append('fullName', formData.fullName);
		formDataToSend.append('email', formData.email);
		formDataToSend.append('mobileNo', formData.mobileNo);
		formDataToSend.append('message', formData.message);
		// formDataToSend.append('followupStatus', formData.followupStatus);
		formDataToSend.append(
			'followupStatus',
			isOtherSelected ? otherFollowupStatus : formData.followupStatus
		);

		// Append custom fields
		formData.customFields.forEach((field) => {
			if (field.fieldType === 'text' && field.fieldValue) {
				formDataToSend.append(`wholesaleText_${field.fieldName}`, field.fieldValue);
			} else if (field.fieldType === 'file' && field.filePath instanceof File) {
				formDataToSend.append(`wholesale_${field.fieldName}`, field.filePath);
			}
		});

		try {
			setLoading(true);
			if (formData.productId) {
				// Delete marked images
				console.log(upcomeinDeleteImage, '=================>');
				await deleteMarkedImages();

				// Update existing product
				const response = await updateWholesale(formDataToSend, formData._id);
				if (response.success) {
					toast.success(response.data.message);
					fetchData();
					handleCloseDialog();
				} else {
					toast.error(response.data.errors);
				}
			}
		} catch (error) {
			console.error('Error:', error);
			toast.error('Failed to save product.');
		} finally {
			setLoading(false);
		}
	};

	const handleCloseDialogWithReset = () => {
		handleCloseDialog();
		setFormData({
			_id: '',
			productId: '',
			quantity: 0,
			fullName: '',
			email: '',
			mobileNo: '',
			message: '',
			status: 1,
			followupStatus: 'New',
			Notification: false,
			createdBy: '',
			createdAt: '',
			updatedAt: '',
			customFields: [],
		});
		setIsOtherSelected(false);
		setOtherFollowupStatus('');
		setUpcomeinDeleteImage([]);
	};

	return (
		<>
			<div style={{ marginTop: '10px' }}>
				<form onSubmit={handleSubmit}>
					<Grid container spacing={2}>
						<Grid size={{ xs: 12 }}>
							<TextField
								label="Full Name"
								name="fullName"
								value={formData.fullName}
								onChange={handleChange}
								fullWidth
								disabled={isDisabled}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<TextField
								label="Email"
								name="email"
								value={formData.email}
								onChange={handleChange}
								fullWidth
								disabled={isDisabled}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<TextField
								label="Mobile No"
								name="mobileNo"
								type="number"
								value={formData.mobileNo}
								onChange={handleChange}
								fullWidth
								// slotProps={{
								// 	htmlInput: {
								// 		maxLength: 10,
								// 		onInput: (event: React.ChangeEvent<HTMLInputElement>) => {
								// 			const input = event.target as HTMLInputElement;
								// 			input.value = input.value.replace(/[^0-9]/g, '');
								// 		},
								// 	},
								// }}
								disabled={isDisabled}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<TextField
								label="Message"
								name="message"
								value={formData.message}
								onChange={handleChange}
								fullWidth
								disabled={isDisabled}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<TextField
								label="Quantity"
								name="quantity"
								value={formData.quantity}
								onChange={handleChange}
								fullWidth
								type="number"
								disabled={isDisabled}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<FormControl fullWidth size="small" disabled={isDisabled}>
								<InputLabel>Follow-up Status</InputLabel>
								<Select
									name="followupStatus"
									value={isOtherSelected ? 'Other' : formData.followupStatus}
									onChange={handleFollowupStatusChange}
									label="Follow-up Status"
									disabled={isDisabled}
								>
									{/* <MenuItem value="New">New</MenuItem>
									<MenuItem value="Under Review">Under Review</MenuItem>
									<MenuItem value="Awaiting Supplier">Awaiting Supplier</MenuItem>
									<MenuItem value="Quotation Sent">Quotation Sent</MenuItem>
									<MenuItem value="Follow-up Pending">Follow-up Pending</MenuItem>
									<MenuItem value="Confirmed">Confirmed</MenuItem>
									<MenuItem value="Closed">Closed</MenuItem>
									<MenuItem value="Cancelled">Cancelled</MenuItem>
									<MenuItem value="Other">Other</MenuItem> */}
									{predefinedOptions.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						{isOtherSelected && (
							<Grid size={{ xs: 12 }}>
								<TextField
									label="Other Follow-up Status"
									name="otherFollowupStatus"
									value={otherFollowupStatus}
									onChange={handleOtherFollowupStatusChange}
									fullWidth
									size="small"
									disabled={isDisabled}
								/>
							</Grid>
						)}
						{formData.customFields.length > 0 && (
							<Typography variant="h5">Customized Fields</Typography>
						)}
						{formData.customFields.length > 0 &&
							formData.customFields.map((field, index) => (
								<Grid size={{ xs: 12 }} key={index}>
									{field.fieldType === 'text' ? (
										<TextField
											label={field.fieldName}
											value={field.fieldValue || ''}
											onChange={(e) =>
												handleCustomFieldChange(index, e.target.value)
											}
											fullWidth
											disabled={isDisabled}
										/>
									) : (
										<div>
											<div style={{ marginBottom: '12px' }}>
												{!isDisabled ? (
													<>
														<input
															type="file"
															id={`productImages-${index}`}
															name="productImages"
															onChange={(e) => {
																const file = e.target.files?.[0];
																if (file) {
																	handleCustomFieldChange(
																		index,
																		file
																	);
																}
															}}
															accept="image/jpeg, image/png, image/jpg"
															style={{ display: 'none' }}
														/>
														<label htmlFor={`productImages-${index}`}>
															<Button
																variant="contained"
																color="primary"
																component="span"
																startIcon={<ImageIcon />}
															>
																{field.fieldName
																	? field.fieldName
																	: 'Upload Image'}
															</Button>
														</label>
													</>
												) : (
													<Typography>{field.fieldName}</Typography>
												)}
												<div>
													{field.filePath && (
														<div
															key={index}
															style={{
																display: 'inline-block',
																position: 'relative',
																margin: '5px',
															}}
														>
															<img
																src={
																	typeof field.filePath ===
																	'string'
																		? field.filePath
																		: URL.createObjectURL(
																				field.filePath
																			)
																}
																alt={field.fieldName}
																style={{
																	width: '100px',
																	height: '100px',
																	objectFit: 'contain',
																}}
															/>
															{/* {!isDisabled && (
																<IconButton
																	style={{
																		position: 'absolute',
																		top: '0',
																		right: '0',
																	}}
																	onClick={() =>
																		handleRemoveCustomField(
																			index,
																			field?.filePath ?? ''
																		)
																	}
																>
																	<DeleteIcon />
																</IconButton>
															)} */}
														</div>
													)}
												</div>
											</div>
										</div>
									)}
								</Grid>
							))}
					</Grid>
				</form>
			</div>
			<DialogActions>
				<Button onClick={handleCloseDialogWithReset} color="primary">
					Cancel
				</Button>
				{!isDisabled && (
					<>
						<Button
							type="submit"
							form="create-contact-form"
							color="primary"
							onClick={handleSubmit}
							variant="contained"
							loading={loading}
							loadingIndicator="Saving…"
						>
							Save
						</Button>
					</>
				)}
			</DialogActions>
			{/* <ToastContainer /> */}
		</>
	);
};

export default CreateProducts;
